import { useRef } from "react";
import { useFrame } from "@react-three/fiber";
import {
  AdditiveBlending,
  DoubleSide,
  InstancedMesh,
  Object3D,
  PointLight,
} from "three";
import { colors } from "../ui/theme";

const PETAL_COUNT = 48;
const DUST_COUNT = 34;
const petalDummy = new Object3D();
const dustDummy = new Object3D();

function GlowPlane({
  position,
  rotation = [0, 0, 0],
  size,
  color,
  opacity,
}: {
  position: [number, number, number];
  rotation?: [number, number, number];
  size: [number, number];
  color: string;
  opacity: number;
}) {
  return (
    <mesh position={position} rotation={rotation}>
      <planeGeometry args={size} />
      <meshBasicMaterial
        color={color}
        transparent
        opacity={opacity}
        depthWrite={false}
        blending={AdditiveBlending}
        side={DoubleSide}
        toneMapped={false}
      />
    </mesh>
  );
}

function CherryPetals() {
  const ref = useRef<InstancedMesh | null>(null);
  const positions = useRef(
    Array.from({ length: PETAL_COUNT }, (_, index) => {
      const side =
        index % 3 === 0
          ? 1
          : index % 3 === 1
            ? -1
            : Math.sign(Math.random() - 0.5) || 1;
      return {
        x: side * (1.65 + Math.random() * 3.4),
        y: Math.random() * 2.8 + 0.55,
        z: -7.3 + Math.random() * 7.2,
        phase: Math.random() * Math.PI * 2,
        speed: 0.075 + Math.random() * 0.12,
        drift: side * (0.04 + Math.random() * 0.08),
      };
    }),
  );

  useFrame((state) => {
    if (!ref.current) return;
    const t = state.clock.elapsedTime;
    for (let i = 0; i < PETAL_COUNT; i++) {
      const p = positions.current[i];
      p.y -= p.speed * 0.016;
      p.x += Math.sin(t * 0.7 + p.phase) * 0.0016 + p.drift * 0.0008;
      if (p.y < -0.15) {
        p.y = 3.35;
        p.x = Math.sign(p.x || 1) * (1.65 + Math.random() * 3.4);
        p.z = -7.3 + Math.random() * 7.2;
      }
      petalDummy.position.set(p.x, p.y, p.z);
      petalDummy.rotation.x = Math.sin(t * 1.1 + p.phase) * 0.75;
      petalDummy.rotation.y = t * 0.45 + p.phase;
      petalDummy.rotation.z = Math.sin(t * 0.75 + p.phase) * 0.5;
      petalDummy.updateMatrix();
      ref.current.setMatrixAt(i, petalDummy.matrix);
    }
    ref.current.instanceMatrix.needsUpdate = true;
  });

  return (
    <instancedMesh ref={ref} args={[undefined, undefined, PETAL_COUNT]}>
      <planeGeometry args={[0.052, 0.035]} />
      <meshBasicMaterial
        color="#d9a9a2"
        transparent
        opacity={0.48}
        side={DoubleSide}
        depthWrite={false}
      />
    </instancedMesh>
  );
}

function DustMotes() {
  const ref = useRef<InstancedMesh | null>(null);
  const positions = useRef(
    Array.from({ length: DUST_COUNT }, () => ({
      x: -2.55 + Math.random() * 2.2,
      y: 0.45 + Math.random() * 1.9,
      z: -3.6 + Math.random() * 2.6,
      phase: Math.random() * Math.PI * 2,
      scale: 0.012 + Math.random() * 0.014,
    })),
  );

  useFrame((state) => {
    if (!ref.current) return;
    const t = state.clock.elapsedTime;
    for (let i = 0; i < DUST_COUNT; i++) {
      const p = positions.current[i];
      dustDummy.position.set(
        p.x + Math.sin(t * 0.32 + p.phase) * 0.035,
        p.y + Math.sin(t * 0.23 + p.phase) * 0.045,
        p.z + Math.cos(t * 0.26 + p.phase) * 0.025,
      );
      dustDummy.scale.setScalar(p.scale);
      dustDummy.updateMatrix();
      ref.current.setMatrixAt(i, dustDummy.matrix);
    }
    ref.current.instanceMatrix.needsUpdate = true;
  });

  return (
    <instancedMesh ref={ref} args={[undefined, undefined, DUST_COUNT]}>
      <sphereGeometry args={[1, 6, 4]} />
      <meshBasicMaterial
        color={colors.amber}
        transparent
        opacity={0.36}
        depthWrite={false}
        blending={AdditiveBlending}
        toneMapped={false}
      />
    </instancedMesh>
  );
}

function FarLayer() {
  return (
    <group name="dojo-far-layer">
      <mesh position={[0, 1.75, -9.8]}>
        <planeGeometry args={[8.8, 4.7]} />
        <meshBasicMaterial color="#1d2c49" side={DoubleSide} />
      </mesh>

      <GlowPlane
        position={[-1.15, 2.85, -9.72]}
        size={[2.05, 2.05]}
        color="#d8d3ba"
        opacity={0.3}
      />
      <mesh position={[-1.15, 2.85, -9.68]}>
        <circleGeometry args={[0.43, 32]} />
        <meshBasicMaterial
          color="#f4efe0"
          side={DoubleSide}
          toneMapped={false}
        />
      </mesh>
      <mesh position={[-1.01, 2.93, -9.67]}>
        <circleGeometry args={[0.43, 32]} />
        <meshBasicMaterial
          color="#1d2c49"
          transparent
          opacity={0.18}
          side={DoubleSide}
        />
      </mesh>

      <mesh position={[0, 0.8, -9.58]}>
        <planeGeometry args={[8.4, 0.58]} />
        <meshBasicMaterial color="#111b2c" side={DoubleSide} />
      </mesh>

      <group position={[2.55, 0, -9.48]}>
        <mesh position={[0, 0.45, 0]}>
          <boxGeometry args={[0.24, 0.72, 0.04]} />
          <meshBasicMaterial color="#080d17" />
        </mesh>
        <mesh position={[0, 0.86, 0]}>
          <boxGeometry args={[0.48, 0.16, 0.04]} />
          <meshBasicMaterial color="#080d17" />
        </mesh>
        <mesh position={[0, 1.03, 0]}>
          <boxGeometry args={[0.34, 0.2, 0.04]} />
          <meshBasicMaterial color="#080d17" />
        </mesh>
        <mesh position={[0, 1.22, 0]}>
          <boxGeometry args={[0.2, 0.18, 0.04]} />
          <meshBasicMaterial color="#080d17" />
        </mesh>
      </group>

      <group position={[1.65, 1.8, -9.46]} rotation={[0, 0, -0.12]}>
        <mesh position={[0, 0, 0]} rotation={[0, 0, 1.18]}>
          <boxGeometry args={[1.2, 0.035, 0.035]} />
          <meshBasicMaterial color="#070b12" />
        </mesh>
        <mesh position={[0.27, 0.17, 0]} rotation={[0, 0, 0.64]}>
          <boxGeometry args={[0.6, 0.025, 0.025]} />
          <meshBasicMaterial color="#070b12" />
        </mesh>
        <mesh position={[-0.28, -0.08, 0]} rotation={[0, 0, 1.72]}>
          <boxGeometry args={[0.52, 0.025, 0.025]} />
          <meshBasicMaterial color="#070b12" />
        </mesh>
      </group>
    </group>
  );
}

function ToriiPortal() {
  return (
    <group position={[0, 0, -6.55]} name="dojo-torii-portal">
      <mesh position={[-0.86, 1.42, 0]}>
        <cylinderGeometry args={[0.085, 0.095, 2.85, 10]} />
        <meshStandardMaterial
          color="#4a130b"
          emissive="#431008"
          emissiveIntensity={0.08}
          roughness={0.85}
        />
      </mesh>
      <mesh position={[0.86, 1.42, 0]}>
        <cylinderGeometry args={[0.085, 0.095, 2.85, 10]} />
        <meshStandardMaterial
          color="#33100a"
          emissive="#351008"
          emissiveIntensity={0.06}
          roughness={0.85}
        />
      </mesh>
      <mesh position={[0, 2.95, 0]}>
        <boxGeometry args={[2.25, 0.15, 0.18]} />
        <meshStandardMaterial
          color="#4f190d"
          emissive="#421007"
          emissiveIntensity={0.08}
          roughness={0.82}
        />
      </mesh>
      <mesh position={[0, 2.55, 0]}>
        <boxGeometry args={[1.92, 0.12, 0.15]} />
        <meshStandardMaterial
          color="#3a120a"
          emissive="#2b0c06"
          emissiveIntensity={0.06}
          roughness={0.85}
        />
      </mesh>
      <GlowPlane
        position={[-0.3, 2.83, 0.08]}
        size={[1.5, 0.08]}
        color={colors.amber}
        opacity={0.08}
      />
    </group>
  );
}

function ShojiPanel({ x }: { x: number }) {
  const side = Math.sign(x) || 1;
  return (
    <group position={[x, 1.55, -4.5]} rotation={[0, side * 0.08, 0]}>
      <mesh>
        <boxGeometry args={[1.05, 2.3, 0.04]} />
        <meshStandardMaterial color="#22180f" roughness={0.92} />
      </mesh>
      {[-0.32, 0, 0.32].map((barX) => (
        <mesh key={`v-${barX}`} position={[barX, 0, 0.035]}>
          <boxGeometry args={[0.025, 2.2, 0.02]} />
          <meshBasicMaterial color={colors.border} transparent opacity={0.42} />
        </mesh>
      ))}
      {[-0.62, -0.2, 0.22, 0.64].map((barY) => (
        <mesh key={`h-${barY}`} position={[0, barY, 0.04]}>
          <boxGeometry args={[0.96, 0.025, 0.02]} />
          <meshBasicMaterial color={colors.border} transparent opacity={0.38} />
        </mesh>
      ))}
    </group>
  );
}

function MidLayer() {
  return (
    <group name="dojo-mid-layer">
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.015, -3.05]}>
        <planeGeometry args={[8.8, 12.2]} />
        <meshBasicMaterial color="#18110c" />
      </mesh>

      {[-3.6, -2.4, -1.2, 1.2, 2.4, 3.6].map((x) => (
        <mesh
          key={x}
          position={[x, 0.003, -3.05]}
          rotation={[-Math.PI / 2, 0, 0]}
        >
          <planeGeometry args={[0.018, 11.5]} />
          <meshBasicMaterial color="#0c0805" transparent opacity={0.55} />
        </mesh>
      ))}
      {[-6.5, -5.15, -3.8, -2.45, -1.1, 0.25, 1.6].map((z) => (
        <mesh key={z} position={[0, 0.004, z]} rotation={[-Math.PI / 2, 0, 0]}>
          <planeGeometry args={[8.8, 0.018]} />
          <meshBasicMaterial color="#6b4e32" transparent opacity={0.24} />
        </mesh>
      ))}

      <mesh position={[0, 0.012, -2.85]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[3.1, 8.2]} />
        <meshBasicMaterial
          color="#030302"
          transparent
          opacity={0.68}
          depthWrite={false}
        />
      </mesh>

      <mesh position={[0, 0.06, -6.72]}>
        <boxGeometry args={[6.3, 0.12, 0.28]} />
        <meshStandardMaterial color={colors.wood} roughness={0.88} />
      </mesh>
      <mesh position={[0, 0.135, -6.48]}>
        <boxGeometry args={[5.7, 0.06, 0.22]} />
        <meshBasicMaterial color={colors.border} transparent opacity={0.56} />
      </mesh>

      {[-3.15, 3.15].map((x) => (
        <group key={x} position={[x, 1.55, -5.15]}>
          <mesh>
            <boxGeometry args={[0.22, 3.1, 0.28]} />
            <meshStandardMaterial color="#2a1f17" roughness={0.9} />
          </mesh>
          <mesh position={[Math.sign(-x) * 0.075, 0, 0.15]}>
            <boxGeometry args={[0.035, 3.0, 0.035]} />
            <meshBasicMaterial
              color={colors.amber}
              transparent
              opacity={x < 0 ? 0.18 : 0.08}
            />
          </mesh>
        </group>
      ))}

      {[
        [-2.65, -2.35],
        [2.65, -2.35],
        [-3.15, -0.15],
        [3.15, -0.15],
      ].map(([x, z]) => (
        <mesh key={`${x}-${z}`} position={[x, 1.45, z]}>
          <boxGeometry args={[0.2, 2.9, 0.24]} />
          <meshStandardMaterial color="#251a11" roughness={0.9} />
        </mesh>
      ))}

      {[-5.65, -3.5, -1.35, 0.8].map((z) => (
        <mesh key={z} position={[0, 3.08, z]}>
          <boxGeometry args={[6.8, 0.16, 0.2]} />
          <meshStandardMaterial color="#2a1f17" roughness={0.88} />
        </mesh>
      ))}
      {[-2.4, 2.4].map((x) => (
        <ShojiPanel key={x} x={x} />
      ))}

      <ToriiPortal />
    </group>
  );
}

function AtmosphereLayer() {
  return (
    <group name="dojo-atmosphere-layer">
      <GlowPlane
        position={[0, 1.25, -7.65]}
        size={[6.6, 1.0]}
        color="#6f7f98"
        opacity={0.095}
      />
      <GlowPlane
        position={[-1.65, 0.7, -3.8]}
        size={[2.6, 0.58]}
        color="#d49b42"
        opacity={0.016}
      />
      <CherryPetals />
      <DustMotes />
    </group>
  );
}

function AndonLantern() {
  const lightRef = useRef<PointLight | null>(null);

  useFrame(({ clock }) => {
    if (!lightRef.current) return;
    const t = clock.elapsedTime;
    lightRef.current.intensity =
      0.52 + Math.sin(t * 5.7) * 0.04 + Math.sin(t * 9.2) * 0.02;
  });

  return (
    <group position={[-2.68, 0.58, -2.35]} name="dojo-andon-lantern">
      <pointLight
        ref={lightRef}
        position={[0, 0.42, 0.06]}
        color={colors.amber}
        distance={3.35}
        decay={2}
      />
      <GlowPlane
        position={[0.28, 0.24, 0.08]}
        rotation={[-Math.PI / 2, 0, 0]}
        size={[1.35, 0.72]}
        color={colors.amber}
        opacity={0.045}
      />
      <GlowPlane
        position={[0.16, 0.45, 0.1]}
        size={[1.15, 1.0]}
        color={colors.amber}
        opacity={0.07}
      />

      <mesh position={[0, 0.32, 0]}>
        <boxGeometry args={[0.44, 0.64, 0.38]} />
        <meshStandardMaterial color="#0b0805" roughness={0.78} />
      </mesh>
      <mesh position={[0, 0.32, 0.205]}>
        <planeGeometry args={[0.34, 0.5]} />
        <meshStandardMaterial
          color="#e9c36a"
          emissive={colors.amber}
          emissiveIntensity={0.85}
          transparent
          opacity={0.86}
          side={DoubleSide}
          roughness={0.6}
        />
      </mesh>
      <mesh position={[0, 0.32, -0.205]} rotation={[0, Math.PI, 0]}>
        <planeGeometry args={[0.34, 0.5]} />
        <meshStandardMaterial
          color="#e9c36a"
          emissive={colors.amber}
          emissiveIntensity={0.45}
          transparent
          opacity={0.72}
          side={DoubleSide}
          roughness={0.6}
        />
      </mesh>
      {[-0.17, 0.17].map((x) => (
        <mesh key={x} position={[x, 0.32, 0.225]}>
          <boxGeometry args={[0.018, 0.5, 0.02]} />
          <meshBasicMaterial color="#5a4326" />
        </mesh>
      ))}
      {[-0.12, 0.12].map((y) => (
        <mesh key={y} position={[0, 0.32 + y, 0.23]}>
          <boxGeometry args={[0.34, 0.018, 0.02]} />
          <meshBasicMaterial color="#5a4326" />
        </mesh>
      ))}
      <mesh position={[0, 0.68, 0]}>
        <boxGeometry args={[0.54, 0.08, 0.48]} />
        <meshStandardMaterial color="#1b120b" roughness={0.82} />
      </mesh>
      <mesh position={[0, -0.04, 0]}>
        <boxGeometry args={[0.5, 0.08, 0.44]} />
        <meshStandardMaterial color="#1b120b" roughness={0.82} />
      </mesh>
    </group>
  );
}

function LightLayer() {
  return (
    <group name="dojo-light-layer">
      <ambientLight intensity={0.22} color="#d8c5a6" />
      <hemisphereLight color="#a9c7ff" groundColor="#130d09" intensity={0.44} />
      <directionalLight
        position={[-1.8, 3.1, -6.8]}
        intensity={0.44}
        color="#a9c7ff"
      />
      <AndonLantern />
    </group>
  );
}

export function Dojo() {
  return (
    <>
      <color attach="background" args={["#100c0a"]} />
      <fog attach="fog" args={["#100c0a", 6.4, 18.0]} />

      <FarLayer />
      <MidLayer />
      <AtmosphereLayer />
      <LightLayer />
    </>
  );
}
